import { useEffect, useState, useRef, useCallback } from "react";
import { io } from "socket.io-client";

const WEBSOCKET_URL = process.env.REACT_APP_WEBSOCKET_URL || "/";
const HISTORY_PAGE_SIZE = 20;

/**
 * Singleton Socket.IO connection to the notifications (utils) service.
 * Authenticates with the shared auth cookie (withCredentials); the service
 * verifies it against the same JWT secret the Admin API uses.
 *
 * Mirrors members/src/socket/useSocket.ts: unread list + count, paginated
 * history (keyset cursor on id), optimistic read/unread/all-read updates.
 */
export function useSocket() {
  const [isConnected, setIsConnected] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [readIds, setReadIds] = useState(new Set());

  // Paginated history shown in the notifications panel
  const [historyItems, setHistoryItems] = useState([]);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  // false until the first history page arrives — lets the panel distinguish
  // "still loading" from "truly empty"
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const socketRef = useRef(null);
  // Mirror of readIds for synchronous checks inside stable callbacks
  const readIdsRef = useRef(new Set());
  // Cursor for the NEXT history page (id of the last loaded item)
  const historyCursorRef = useRef(null);
  // Cursor of the request currently in flight; undefined = none pending.
  // null is meaningful (= first-page/reset request).
  const pendingHistoryCursorRef = useRef(undefined);
  const historyLoadedRef = useRef(false);

  useEffect(() => {
    if (!socketRef.current) {
      socketRef.current = io(WEBSOCKET_URL, {
        autoConnect: true,
        withCredentials: true,
      });
    }

    const socket = socketRef.current;

    const onConnect = () => {
      setIsConnected(true);
      socket.emit("get_unread_notifications");
    };

    const onDisconnect = () => {
      setIsConnected(false);
    };

    const onNewNotification = () => {
      // Refresh the full unread list to get complete data (params for
      // template interpolation)
      socket.emit("get_unread_notifications");
      // If the panel already loaded history, refresh its first page so the
      // new item shows up live. Skip when a request is already pending.
      if (
        historyLoadedRef.current &&
        pendingHistoryCursorRef.current === undefined
      ) {
        pendingHistoryCursorRef.current = null;
        socket.emit("get_notifications_history", {
          limit: HISTORY_PAGE_SIZE,
          cursor: null,
        });
      }
    };

    const onUnreadNotifications = (data) => {
      setUnreadNotifications(data);
      setUnreadNotificationsCount(data.length);
    };

    const onNotificationsHistory = (data) => {
      const requestedCursor = pendingHistoryCursorRef.current;
      pendingHistoryCursorRef.current = undefined;
      historyCursorRef.current = data.nextCursor;
      historyLoadedRef.current = true;
      setIsHistoryLoading(false);
      setHistoryLoaded(true);
      setHasMoreHistory(data.nextCursor !== null);
      setUnreadNotificationsCount(data.unreadCount);
      setHistoryItems((prev) => {
        // First page (or refresh) replaces; subsequent pages append.
        if (!requestedCursor) return data.items;
        const seen = new Set(prev.map((n) => n.id));
        return [...prev, ...data.items.filter((n) => !seen.has(n.id))];
      });
    };

    const onNotificationRead = ({ recipientId }) => {
      // Server confirmation — the badge was already decremented optimistically
      if (!readIdsRef.current.has(recipientId)) {
        readIdsRef.current = new Set(readIdsRef.current).add(recipientId);
        setReadIds(readIdsRef.current);
      }
    };

    const onAllNotificationsRead = () => {
      setUnreadNotificationsCount(0);
      setHistoryItems((prev) =>
        prev.map((n) => (n.is_read ? n : { ...n, is_read: true })),
      );
    };

    const onNotificationUnread = ({ recipientId }) => {
      if (readIdsRef.current.has(recipientId)) {
        const next = new Set(readIdsRef.current);
        next.delete(recipientId);
        readIdsRef.current = next;
        setReadIds(next);
      }
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("new_notification", onNewNotification);
    socket.on("unread_notifications", onUnreadNotifications);
    socket.on("notifications_history", onNotificationsHistory);
    socket.on("notification_read", onNotificationRead);
    socket.on("all_notifications_read", onAllNotificationsRead);
    socket.on("notification_unread", onNotificationUnread);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("new_notification", onNewNotification);
      socket.off("unread_notifications", onUnreadNotifications);
      socket.off("notifications_history", onNotificationsHistory);
      socket.off("notification_read", onNotificationRead);
      socket.off("all_notifications_read", onAllNotificationsRead);
      socket.off("notification_unread", onNotificationUnread);
    };
  }, []);

  /**
   * Loads a page of the notifications history. `reset: true` (re)loads the
   * first page; otherwise continues from the last cursor. No-op when a
   * request is already in flight or there is nothing more to load.
   */
  const loadNotificationsHistory = useCallback((options) => {
    const socket = socketRef.current;
    if (!socket) return;
    if (pendingHistoryCursorRef.current !== undefined) return;

    const cursor = options?.reset ? null : historyCursorRef.current;
    if (!options?.reset && cursor === null) return; // no more pages

    pendingHistoryCursorRef.current = cursor;
    setIsHistoryLoading(true);
    socket.emit("get_notifications_history", {
      limit: HISTORY_PAGE_SIZE,
      cursor,
    });
  }, []);

  /**
   * Marks a notification as read (persists a notifications_views row on the
   * server). Optimistic: dot and badge update immediately; already-read
   * items are a no-op.
   */
  const markNotificationRead = useCallback((notification) => {
    if (notification.is_read || readIdsRef.current.has(notification.id)) {
      return;
    }
    readIdsRef.current = new Set(readIdsRef.current).add(notification.id);
    setReadIds(readIdsRef.current);
    setHistoryItems((prev) =>
      prev.map((n) =>
        n.id === notification.id && !n.is_read ? { ...n, is_read: true } : n,
      ),
    );
    setUnreadNotificationsCount((prev) => Math.max(0, prev - 1));
    socketRef.current?.emit("mark_read", {
      recipientId: notification.id,
      creationTime: notification.creation_time,
    });
  }, []);

  /**
   * Marks a read notification back as unread — the server deletes this
   * user's view row. Optimistic: dot and badge update immediately.
   */
  const markNotificationUnread = useCallback((notification) => {
    const wasRead =
      notification.is_read === true || readIdsRef.current.has(notification.id);
    if (!wasRead) return;
    if (readIdsRef.current.has(notification.id)) {
      const next = new Set(readIdsRef.current);
      next.delete(notification.id);
      readIdsRef.current = next;
      setReadIds(next);
    }
    setHistoryItems((prev) =>
      prev.map((n) =>
        n.id === notification.id && n.is_read ? { ...n, is_read: false } : n,
      ),
    );
    setUnreadNotificationsCount((prev) => prev + 1);
    socketRef.current?.emit("mark_unread", {
      recipientId: notification.id,
    });
  }, []);

  /**
   * Marks ALL unread notifications of the user as read in one server-side
   * statement (also the ones beyond the loaded pages).
   */
  const markAllNotificationsRead = useCallback(() => {
    setUnreadNotificationsCount(0);
    setHistoryItems((prev) =>
      prev.map((n) => (n.is_read ? n : { ...n, is_read: true })),
    );
    socketRef.current?.emit("mark_all_read");
  }, []);

  const emit = useCallback(
    (event, data) => socketRef.current?.emit(event, data),
    [],
  );

  const on = useCallback(
    (event, callback) => socketRef.current?.on(event, callback),
    [],
  );

  const off = useCallback(
    (event, callback) =>
      callback
        ? socketRef.current?.off(event, callback)
        : socketRef.current?.removeAllListeners(event),
    [],
  );

  return {
    socket: socketRef.current,
    isConnected,
    emit,
    on,
    off,
    unreadNotifications,
    unreadNotificationsCount,
    readIds,
    markNotificationRead,
    markNotificationUnread,
    markAllNotificationsRead,
    historyItems,
    hasMoreHistory,
    isHistoryLoading,
    historyLoaded,
    loadNotificationsHistory,
  };
}
